// app/(routes)/tools/ai-for-financial-advisors/components/WorkflowReport.tsx
"use client";

import {
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardCopy,
  Database,
  FileSearch,
  GraduationCap,
  Hammer,
  Layers,
  ListChecks,
  Mail,
  Printer,
  RefreshCw,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Target,
  Users,
  WalletCards,
} from "lucide-react";
import Link from "next/link";
import { FormEvent, useState } from "react";

import { HTFButton } from "@/app/shared/components/button/HTFButtonComponent";
import { cn } from "@/lib/utils";

import { recordReportEvent } from "../workflow-finder.analytics";
import type { WorkflowRecommendation } from "../workflow-finder.types";
import {
  DarkGridBackdrop,
  DetailRow,
  Eyebrow,
  FactTile,
  IconChip,
  Panel,
  RankChip,
  SectionHead,
} from "./report-ui";

const DELIVERY_ICONS = [SlidersHorizontal, Hammer, GraduationCap, RefreshCw];

export interface WorkflowReportProps {
  result: WorkflowRecommendation;
  /** "page" owns its own page container and renders a full-bleed hero. */
  variant?: "page" | "embedded";
  mode?: "interactive" | "shared";
  createdAt?: string | null;
  firmName?: string;
  onStartOver?: () => void;
  /** Returns the status message to show under the form. */
  onIdentify?: (name: string, email: string) => Promise<string>;
  onHighIntent?: (
    intent: "contact" | "solution" | "proposal",
    target: string,
  ) => void;
}

export function WorkflowReport({
  result,
  variant = "embedded",
  mode = "interactive",
  createdAt,
  firmName,
  onStartOver,
  onIdentify,
  onHighIntent,
}: WorkflowReportProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [shareStatus, setShareStatus] = useState("");

  const primary = result.opportunities[0];
  const alternatives = result.opportunities.slice(1);
  const isPage = variant === "page";
  const HeroHeading = isPage ? "h1" : "h2";
  const container = "mx-auto w-full max-w-6xl px-5 sm:px-8";
  const displayFirmName = firmName || result.company_profile.name;

  async function copyPlan() {
    const text = [
      `${result.title}: ${result.company_profile.name}`,
      `Start with: ${primary.title}`,
      `Why: ${primary.why_now}`,
      `First useful output: ${primary.first_output}`,
      "What to do this week:",
      ...primary.this_week.map((item, index) => `${index + 1}. ${item}`),
      "First 30 days:",
      ...result.pilot_plan.map(
        (item) => `${item.week}, ${item.title}: ${item.outcome}`,
      ),
      `Measure: ${primary.metric}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("Plan copied");
    } catch {
      setCopyStatus("Could not copy. Use Print or save as PDF instead.");
    }
  }

  async function shareReport() {
    if (!result.share) return;
    const url = new URL(result.share.path, window.location.origin).toString();
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${result.company_profile.name} AI opportunity map`,
          text: "A practical AI opportunity map with a controlled 30-day pilot.",
          url,
        });
        setShareStatus("Shared");
        await recordReportEvent(result.share.slug, "report_share", {
          eventId: crypto.randomUUID(),
          metadata: { method: "native_share" },
        });
      } else {
        await navigator.clipboard.writeText(url);
        setShareStatus("Share link copied");
        await recordReportEvent(result.share.slug, "report_share", {
          eventId: crypto.randomUUID(),
          metadata: { method: "copy_link" },
        });
      }
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") {
        return;
      }
      setShareStatus("Could not share. Copy the address from your browser.");
    }
  }

  function trackSharedReportCta(target: string) {
    if (!result.share) return;
    void recordReportEvent(result.share.slug, "report_cta_click", {
      eventId: crypto.randomUUID(),
      metadata: { target },
    });
  }

  async function submitEmail(event: FormEvent) {
    event.preventDefault();
    if (!onIdentify || !name.trim() || !email.trim()) return;
    setEmailStatus("Sending your copy…");
    setEmailStatus(await onIdentify(name.trim(), email.trim()));
  }

  const facts: Array<[typeof Building2, string, string]> = [
    [Building2, "Company", result.company_profile.name],
    [Database, "Type", result.company_profile.organization_type],
    [WalletCards, "Scale", result.company_profile.aum],
    [
      Users,
      "Team",
      String(
        result.company_profile.team_size ||
          result.company_profile.advisor_count ||
          "Not provided",
      ),
    ],
  ];

  return (
    <section
      id="workflow-result"
      data-testid="advisor-workflow-finder-result-memo"
      className="scroll-mt-24"
    >
      {/* Hero: identity, headline, the facts the map was built on, and controls. */}
      <div
        className={cn(
          "relative overflow-hidden bg-text text-on-dark",
          !isPage && "rounded-[2rem]",
        )}
      >
        <DarkGridBackdrop />
        <div
          className={cn(
            "relative z-10",
            isPage ? `${container} py-12 sm:py-16` : "p-6 sm:p-10",
          )}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-on-dark/10 px-4 py-1.5 font-heading text-xs font-black uppercase tracking-[0.16em] text-primary ring-1 ring-on-dark/15">
              {mode === "shared" ? (
                <Share2 size={13} aria-hidden="true" />
              ) : (
                <CheckCircle2 size={13} aria-hidden="true" />
              )}
              {mode === "shared"
                ? "Shared AI opportunity map"
                : "Useful now, no email required"}
            </span>
            {createdAt && (
              <span className="text-xs text-on-dark/50">
                Created {createdAt}
              </span>
            )}
          </div>

          {/* The shared report owns the page, so it carries the h1. Inside the
              tool the landing hero already does. */}
          <HeroHeading className="mt-6 max-w-4xl font-heading text-4xl font-black leading-[1.02] tracking-tight sm:text-5xl">
            {result.title}
          </HeroHeading>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-on-dark/70">
            {result.summary}
          </p>

          <div className="mt-9 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {facts.map(([Icon, label, value]) => (
              <FactTile key={label} icon={Icon} label={label} value={value} />
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2 print:hidden">
            {result.share && (
              <button
                type="button"
                onClick={shareReport}
                data-testid="advisor-workflow-finder-share"
                className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 font-heading text-sm font-black text-text transition hover:brightness-95"
              >
                <Share2 size={15} aria-hidden="true" />
                Share this map
              </button>
            )}
            <button
              type="button"
              onClick={copyPlan}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg px-4 font-heading text-sm font-bold text-on-dark/80 ring-1 ring-on-dark/20 transition hover:bg-on-dark/10 hover:text-on-dark"
            >
              <ClipboardCopy size={15} aria-hidden="true" />
              Copy plan
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg px-4 font-heading text-sm font-bold text-on-dark/80 ring-1 ring-on-dark/20 transition hover:bg-on-dark/10 hover:text-on-dark"
            >
              <Printer size={15} aria-hidden="true" />
              Print or save PDF
            </button>
            {(copyStatus || shareStatus) && (
              <p
                aria-live="polite"
                className="text-xs font-semibold text-primary"
              >
                {shareStatus || copyStatus}
              </p>
            )}
          </div>
        </div>
      </div>

      <div
        className={cn(
          "space-y-14 sm:space-y-20",
          isPage ? `${container} py-14 sm:py-20` : "mt-12 sm:mt-16",
        )}
      >
        {/* The recommendation itself. The only accent bar on the page. */}
        <section>
          <SectionHead
            eyebrow="Start here"
            eyebrowIcon={Target}
            title={primary.title}
            description={primary.summary}
          />

          <div className="mt-7 grid gap-5 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
            <Panel padding="none" className="overflow-hidden shadow-sm">
              <div className="h-1.5 bg-primary" aria-hidden="true" />
              <div className="p-5 sm:p-7">
                <div className="rounded-2xl bg-primary/15 p-5">
                  <Eyebrow icon={Sparkles} className="text-primary-dark">
                    First useful output
                  </Eyebrow>
                  <p className="mt-3 font-heading text-lg font-black leading-snug text-text">
                    {primary.first_output}
                  </p>
                </div>

                <p className="mt-7 font-heading text-sm font-black uppercase tracking-wider text-text-tertiary">
                  What to do this week
                </p>
                <ol className="mt-2 divide-y divide-border">
                  {primary.this_week.map((item, index) => (
                    <DetailRow
                      key={item}
                      leading={
                        <RankChip tone="quiet">{`0${index + 1}`}</RankChip>
                      }
                      title={item}
                    />
                  ))}
                </ol>
              </div>
            </Panel>

            <div className="space-y-5">
              <Panel>
                <Eyebrow icon={ListChecks}>Why this fits</Eyebrow>
                <p className="mt-3 text-sm leading-relaxed text-text-secondary">
                  {primary.why_now}
                </p>
                <ul className="mt-5 space-y-3 border-t border-border pt-5">
                  {primary.fit_signals.map((signal) => (
                    <li
                      key={signal}
                      className="flex gap-3 text-sm leading-relaxed text-text-secondary"
                    >
                      <Check
                        size={15}
                        className="mt-0.5 shrink-0 text-primary-dark"
                        aria-hidden="true"
                      />
                      {signal}
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel>
                <Eyebrow icon={ShieldCheck}>Review controls</Eyebrow>
                <ul className="mt-3 divide-y divide-border">
                  {primary.guardrails.map((item) => (
                    <li
                      key={item}
                      className="py-3 text-sm leading-relaxed text-text-secondary first:pt-0 last:pb-0"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </Panel>

              <div className="rounded-2xl bg-text px-5 py-5 text-on-dark">
                <Eyebrow className="text-primary">
                  Establish this measure
                </Eyebrow>
                <p className="mt-2 font-heading text-sm font-bold leading-snug">
                  {primary.metric}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div
          data-testid="advisor-workflow-finder-complete-value"
          className="grid gap-10 lg:grid-cols-2 lg:gap-8"
        >
          <section>
            <SectionHead
              eyebrow="First 30 days"
              title="Prove usefulness before deeper automation."
            />
            <Panel className="mt-6">
              <ol className="relative space-y-6 border-l border-border pl-6">
                {result.pilot_plan.map((item) => (
                  <li key={item.week} className="relative">
                    <span
                      aria-hidden="true"
                      className="absolute left-[-1.9rem] top-1 h-3 w-3 rounded-full bg-secondary ring-4 ring-secondary/20"
                    />
                    <p className="font-heading text-xs font-black uppercase tracking-wider text-secondary-dark">
                      {item.week}
                    </p>
                    <p className="mt-1 font-heading text-base font-bold text-text">
                      {item.title}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                      {item.outcome}
                    </p>
                  </li>
                ))}
              </ol>
            </Panel>
          </section>

          <section>
            <SectionHead
              eyebrow="System handoff"
              title="Keep the existing stack. Add a controlled working layer."
            />
            <Panel className="mt-6" padding="sm">
              <div className="divide-y divide-border">
                {result.system_handoff.map((item) => (
                  <DetailRow
                    key={item.stage}
                    leading={<RankChip tone="teal">{item.stage}</RankChip>}
                    title={item.title}
                    detail={item.detail}
                  />
                ))}
              </div>
            </Panel>
          </section>
        </div>

        {alternatives.length > 0 && (
          <section>
            <SectionHead
              eyebrow="Next in the backlog"
              eyebrowIcon={Layers}
              title="What follows once the first workflow is real."
            />
            <Panel className="mt-6" padding="sm">
              <div className="divide-y divide-border">
                {alternatives.map((opportunity) => (
                  <details key={opportunity.code} className="group">
                    <summary className="flex cursor-pointer list-none items-center gap-4 py-4 transition hover:opacity-80">
                      <RankChip tone="quiet">{`0${opportunity.rank}`}</RankChip>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-heading text-base font-bold text-text">
                          {opportunity.title}
                        </h3>
                        <p className="mt-1 text-sm leading-snug text-text-secondary">
                          {opportunity.first_output}
                        </p>
                      </div>
                      <ChevronDown
                        aria-hidden="true"
                        size={18}
                        className="shrink-0 text-text-tertiary transition group-open:rotate-180"
                      />
                    </summary>
                    <div className="grid gap-5 pb-5 pl-12 sm:grid-cols-2">
                      <div>
                        <p className="font-heading text-xs font-black uppercase tracking-wider text-text-tertiary">
                          Why it follows
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                          {opportunity.why_now}
                        </p>
                      </div>
                      <div>
                        <p className="font-heading text-xs font-black uppercase tracking-wider text-text-tertiary">
                          Measure
                        </p>
                        <p className="mt-2 text-sm font-semibold text-text">
                          {opportunity.metric}
                        </p>
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            </Panel>
          </section>
        )}

        <section>
          <SectionHead
            eyebrow="From opportunity to operating capability"
            title="A useful pilot needs a plan, a production build, trained operators, and an improvement loop."
            description="This is the implementation path behind the recommendation. You can use it with your own team. It is also how HowTheF takes workflows from mapped process to a governed internal AI operating layer."
          />
          <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {result.delivery_path.map((item, index) => {
              const DeliveryIcon = DELIVERY_ICONS[index] || SlidersHorizontal;
              return (
                <Panel
                  key={item.phase}
                  padding="sm"
                  className="flex flex-col shadow-sm"
                >
                  <IconChip icon={DeliveryIcon} tone="teal" />
                  <p className="mt-4 font-heading text-xs font-black uppercase tracking-wider text-secondary-dark">
                    {item.phase} {item.title}
                  </p>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-text-secondary">
                    {item.detail}
                  </p>
                  <p className="mt-4 border-t border-border pt-3 text-xs font-semibold leading-relaxed text-text">
                    {item.deliverable}
                  </p>
                </Panel>
              );
            })}
          </div>
        </section>

        <details className="group rounded-2xl bg-surface-light px-5 py-4 sm:px-7">
          <summary className="flex cursor-pointer list-none items-center gap-3">
            <IconChip icon={FileSearch} tone="neutral" size="sm" />
            <span className="flex-1 font-heading text-base font-bold text-text">
              Evidence, assumptions, and what we did not assess
            </span>
            <ChevronDown
              size={18}
              aria-hidden="true"
              className="shrink-0 text-text-tertiary transition group-open:rotate-180"
            />
          </summary>
          <div className="mt-5 grid gap-8 border-t border-border pt-5 lg:grid-cols-2">
            <div className="space-y-4">
              {result.evidence.map((item) => (
                <div key={item.source}>
                  <p className="font-heading text-sm font-bold text-text">
                    {item.source}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-text-secondary">
                    {item.use}
                  </p>
                </div>
              ))}
            </div>
            <div>
              <p className="font-heading text-sm font-bold text-text">
                What we did not assess
              </p>
              <ul className="mt-3 space-y-3">
                {result.unknowns.map((item) => (
                  <li
                    key={item}
                    className="text-xs leading-relaxed text-text-secondary"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </details>

        {mode === "interactive" ? (
          <section className="grid overflow-hidden rounded-[2rem] bg-surface-light lg:grid-cols-2">
            <form onSubmit={submitEmail} className="p-6 sm:p-9">
              <div className="flex items-center gap-3">
                <IconChip icon={Mail} tone="teal" size="sm" />
                <p className="font-heading text-xl font-black text-text">
                  Keep a copy
                </p>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-text-secondary">
                Optional. The complete result is already yours above.
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  placeholder="Your name"
                  className="h-12 rounded-xl bg-layer-1 px-4 text-sm outline-none ring-1 ring-border transition focus:ring-2 focus:ring-ring"
                />
                <input
                  data-testid="advisor-workflow-finder-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  placeholder="Work email"
                  className="h-12 rounded-xl bg-layer-1 px-4 text-sm outline-none ring-1 ring-border transition focus:ring-2 focus:ring-ring"
                />
              </div>
              <HTFButton
                data-testid="advisor-workflow-finder-email-submit"
                type="submit"
                variant="secondary-outline"
                className="mt-4"
              >
                Send my copy
                <ArrowRight size={15} />
              </HTFButton>
              {emailStatus && (
                <p
                  data-testid="advisor-workflow-finder-email-success"
                  aria-live="polite"
                  className="mt-3 text-xs text-text-secondary"
                >
                  {emailStatus}
                </p>
              )}
            </form>

            <div className="relative overflow-hidden bg-text p-6 text-on-dark sm:p-9">
              <DarkGridBackdrop />
              <div className="relative z-10">
                <Eyebrow className="text-primary">
                  Explore this with HowTheF
                </Eyebrow>
                <h3 className="mt-3 font-heading text-2xl font-black leading-tight">
                  Pressure-test the plan before you spend months on the wrong
                  build.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-on-dark/70">
                  We can map the real workflow, build it into your systems,
                  train the people who run it, and establish the evaluation
                  loop.
                </p>
                <Link
                  data-testid="advisor-workflow-finder-high-intent"
                  href="/contact?source=advisor-opportunity-map"
                  onClick={() =>
                    onHighIntent?.("contact", "pressure-test-plan")
                  }
                  className="mt-7 inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 font-heading text-sm font-black text-text transition hover:brightness-95"
                >
                  Pressure-test this plan
                  <ArrowRight size={15} />
                </Link>
                {result.proposal_url && (
                  <Link
                    href={result.proposal_url}
                    onClick={() =>
                      onHighIntent?.("proposal", result.proposal_url || "")
                    }
                    className="mt-5 flex w-fit items-center gap-2 text-xs font-bold text-primary hover:underline"
                  >
                    View the page prepared for {displayFirmName}
                    <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </div>
          </section>
        ) : (
          <section className="grid overflow-hidden rounded-[2rem] bg-surface-light lg:grid-cols-[0.9fr_1.1fr]">
            <div className="p-6 sm:p-9">
              <Eyebrow>Make it yours</Eyebrow>
              <h3 className="mt-3 font-heading text-2xl font-black leading-tight text-text">
                Find the workflow worth building at your company.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-text-secondary">
                Start with a company name. No CRD number or email is required to
                get the complete map.
              </p>
              <Link
                href="/tools/ai-for-financial-advisors?source=shared-report"
                onClick={() => trackSharedReportCta("create-own-map")}
                className="mt-7 inline-flex min-h-11 items-center gap-2 rounded-lg bg-secondary px-5 font-heading text-sm font-black text-on-dark transition hover:brightness-95"
              >
                Create your own map
                <ArrowRight size={15} />
              </Link>
            </div>
            <div className="relative overflow-hidden bg-text p-6 text-on-dark sm:p-9">
              <DarkGridBackdrop />
              <div className="relative z-10">
                <Eyebrow className="text-primary">
                  Explore this with HowTheF
                </Eyebrow>
                <h3 className="mt-3 font-heading text-2xl font-black leading-tight">
                  Turn the brief into a governed operating capability.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-on-dark/70">
                  We plan the real workflow, build it into your systems, train
                  the operators, and improve it against measured evidence.
                </p>
                <Link
                  href="/contact?source=shared-advisor-opportunity-map"
                  onClick={() => trackSharedReportCta("pressure-test-plan")}
                  className="mt-7 inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 font-heading text-sm font-black text-text transition hover:brightness-95"
                >
                  Pressure-test this plan
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </section>
        )}

        {result.relevant_solutions.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {result.relevant_solutions.slice(0, 2).map((solution) => (
              <Link
                key={solution.slug}
                href={solution.url}
                onClick={() => onHighIntent?.("solution", solution.slug)}
                className="group flex items-center justify-between gap-5 rounded-2xl bg-surface-light px-5 py-4 transition hover:shadow-sm"
              >
                <div>
                  <p className="font-heading text-xs font-black uppercase tracking-wider text-text-tertiary">
                    Related implementation
                  </p>
                  <p className="mt-1 font-heading text-base font-bold text-text group-hover:text-link">
                    {solution.name}
                  </p>
                </div>
                <ArrowRight
                  size={17}
                  className="shrink-0 text-link transition group-hover:translate-x-0.5"
                />
              </Link>
            ))}
          </div>
        )}

        {mode === "interactive" && onStartOver && (
          <button
            type="button"
            onClick={onStartOver}
            className="min-h-11 rounded-lg px-2 text-xs font-semibold text-text-secondary underline underline-offset-2 hover:bg-surface"
          >
            Start over with different answers
          </button>
        )}
      </div>
    </section>
  );
}
